import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

export default function AdminSettings({ showToast }) {
  const { authFetch } = useAuth();
  const [flags, setFlags] = useState([]);
  const [health, setHealth] = useState(null);
  const [editVal, setEditVal] = useState({});

  useEffect(() => {
    authFetch('/api/admin/flags').then(r => r.json()).then(d => { if (Array.isArray(d)) setFlags(d); });
    authFetch('/api/admin/health').then(r => r.json()).then(d => setHealth(d)).catch(() => {});
  }, [authFetch]);

  const updateFlag = async (key, value) => {
    const res = await authFetch(`/api/admin/flags/${key}`, { method: 'PUT', body: JSON.stringify({ value }) });
    if (res.ok) {
      setFlags(prev => prev.map(f => f.key === key ? { ...f, value } : f));
      showToast(`Flag "${key}" updated`);
    } else showToast('Update failed', 'error');
  };

  const isBool = (v) => v === 'true' || v === 'false';

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="page-title">Platform Settings</div>
          <span className="admin-sidebar-badge">ADMIN</span>
        </div>
        <div className="page-sub">Feature flags, system health, and platform configuration</div>
      </div>

      {/* System Health */}
      {health && (
        <>
          <div className="sec-header"><div className="sec-title">System Health</div><div className="sec-line"></div></div>
          <div className="g4" style={{ marginBottom: 28 }}>
            {[
              { label: 'Status', val: health.status === 'ok' ? '✅ OK' : '❌ Error', color: health.status === 'ok' ? 'var(--green)' : 'var(--red)' },
              { label: 'Uptime', val: Math.round(health.uptime / 60) + 'm', color: 'var(--gold-2)' },
              { label: 'DB Size', val: (health.db_size_bytes / 1024).toFixed(0) + ' KB', color: 'var(--gold)' },
              { label: 'Node', val: health.node_version, color: 'var(--text2)' },
            ].map(s => (
              <div key={s.label} className="card" style={{ padding: '14px 18px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--ff-m)', fontSize: 18, fontWeight: 500, color: s.color, marginBottom: 4 }}>{s.val}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Feature Flags */}
      <div className="sec-header"><div className="sec-title">Feature Flags</div><div className="sec-line"></div><span className="tag">Live changes — no restart needed</span></div>
      <div className="card" style={{ marginBottom: 24 }}>
        {flags.map(f => (
          <div key={f.key} className="flag-row">
            <div style={{ flex: 1 }}>
              <div className="flag-key">{f.key}</div>
              <div className="flag-desc">{f.description}</div>
            </div>
            {isBool(f.value) ? (
              <div className={`toggle ${f.value === 'true' ? 'on' : ''}`}
                onClick={() => updateFlag(f.key, f.value === 'true' ? 'false' : 'true')}
                title={f.value} />
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="form-input" style={{ width: 80, padding: '4px 8px', fontSize: 12 }}
                  value={editVal[f.key] ?? f.value}
                  onChange={e => setEditVal(prev => ({ ...prev, [f.key]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && updateFlag(f.key, editVal[f.key] ?? f.value)} />
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                  onClick={() => updateFlag(f.key, editVal[f.key] ?? f.value)}>Save</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Env guide */}
      <div className="sec-header"><div className="sec-title">Environment Setup Guide</div><div className="sec-line"></div></div>
      <div className="card" style={{ background: 'var(--bg3)' }}>
        {[
          { key: 'ANTHROPIC_API_KEY', where: 'console.anthropic.com', status: '→ Enables AI Collaborator + report narratives' },
          { key: 'RAZORPAY_KEY_ID + SECRET', where: 'dashboard.razorpay.com', status: '→ Enables real payments (demo mode works without)' },
          { key: 'RAZORPAY_WEBHOOK_SECRET', where: 'Razorpay Dashboard → Webhooks', status: '→ Server-side payment verification (critical for production)' },
          { key: 'SMTP_HOST + USER + PASS', where: 'Gmail App Password or SendGrid', status: '→ Enables welcome emails, password reset, notifications' },
          { key: 'JWT_SECRET', where: 'Any random 32+ char string', status: '→ Change from default before going live' },
        ].map(e => (
          <div key={e.key} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ fontFamily: 'var(--ff-m)', fontSize: 11, color: 'var(--gold-2)', width: 260, flexShrink: 0 }}>{e.key}</div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>{e.where}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{e.status}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
